import React, { useEffect, useState } from 'react';
import { Container } from 'reactstrap';
import { Link } from 'react-router-dom';
import t, { partial } from '../../util/translation/translation';
import SegmentsShowcase from '../users/components/SegmentsShowcase';
import TiersShowcase from '../user_tiers/components/TiersShowcase';
import ClubheartShowcase from '../goalSponsor/components/ClubheartShowcase';
import AuctionsShowcase from '../auctions/components/AuctionsShowcase';
import ChatsShowcase from '../chat/components/ChatsShowcase';
import QuestionnairesShowcase from '../questionaires/components/QuestionnairesShowcase';
import LotteryShowcase from '../newLottery/components/LotteryShowcase';
import DealsShowcase from '../deals/components/DealsShowcase';
import AppLayoutShowcase from '../app_layout/AppLayoutShowcase';
import NewsShowcase from '../news/components/NewsShowcase';
import SpotlightShowcase from '../Spotlight/components/SpotlightShowcase';
import MessagesShowcase from '../messages/components/MessagesShowcase';
import OffersShowcase from '../offers/components/OffersShowcase';
import EventsShowcase from '../events/components/EventsShowcase';

/**
 * Learn more (sidebar, under Dashboard): the feature introductions shown on
 * their own pages, collected and always open, each with a link to its page.
 * A sticky menu jumps between them and marks the one being read.
 */
const LearnPage = () => {
  const s = partial('shared');
  const [current, setCurrent] = useState('layout');

  const goTo = (route, label) => (
    <Link to={route} className="btn btn-outline-primary btn-sm mb-0 learn__go">
      {label}
    </Link>
  );

  // The everyday tools first, then the ones that raise money or engage
  const sections = [
    {
      id: 'layout',
      label: t('SidebarContent.appLayout'),
      content: (
        <AppLayoutShowcase
          collapsible={false}
          action={goTo('/app_layout', s('learnGoToAppLayout'))}
        />
      ),
    },
    {
      id: 'news',
      label: s('news'),
      content: (
        <NewsShowcase
          collapsible={false}
          action={goTo('/news', s('learnGoToNews'))}
        />
      ),
    },
    {
      id: 'spotlight',
      label: s('spotlight'),
      content: (
        <SpotlightShowcase
          collapsible={false}
          action={goTo('/spotlight', s('learnGoToSpotlight'))}
        />
      ),
    },
    {
      id: 'messages',
      label: s('communication'),
      content: (
        <MessagesShowcase
          collapsible={false}
          action={goTo('/messages', s('learnGoToMessages'))}
        />
      ),
    },
    {
      id: 'offers',
      label: t('OffersPage.offers'),
      content: (
        <OffersShowcase
          collapsible={false}
          action={goTo('/offers', s('learnGoToOffers'))}
        />
      ),
    },
    {
      id: 'events',
      label: s('events'),
      content: (
        <EventsShowcase
          collapsible={false}
          action={goTo('/events', s('learnGoToEvents'))}
        />
      ),
    },
    {
      id: 'tiers',
      label: s('userTiers'),
      content: (
        <TiersShowcase
          collapsible={false}
          action={goTo('/user_tiers?tab=1', s('learnGoToTiers'))}
        />
      ),
    },
    {
      id: 'auctions',
      label: s('auctions'),
      content: (
        <AuctionsShowcase
          collapsible={false}
          action={goTo('/auctions', s('learnGoToAuctions'))}
        />
      ),
    },
    {
      id: 'lottery',
      label: s('lottery'),
      content: (
        <LotteryShowcase
          collapsible={false}
          action={goTo('/lottery', s('learnGoToLottery'))}
        />
      ),
    },
    {
      id: 'deals',
      label: s('deals'),
      content: (
        <DealsShowcase
          collapsible={false}
          action={goTo('/deals', s('learnGoToDeals'))}
        />
      ),
    },
    {
      id: 'clubheart',
      label: t('goalSponsor.goalSponsor'),
      content: (
        <ClubheartShowcase
          collapsible={false}
          action={goTo('/goal-sponsor', s('learnGoToClubheart'))}
        />
      ),
    },
    {
      id: 'questionnaires',
      label: t('questionaire.questionaires'),
      content: (
        <QuestionnairesShowcase
          collapsible={false}
          action={goTo('/questionnaires', s('learnGoToQuestionnaires'))}
        />
      ),
    },
    {
      id: 'chats',
      label: s('learnNavChats'),
      content: (
        <ChatsShowcase
          collapsible={false}
          action={goTo('/chats', s('learnGoToChats'))}
        />
      ),
    },
    {
      id: 'segments',
      label: t('HighlightBannerPage.segments'),
      content: (
        <SegmentsShowcase
          collapsible={false}
          action={goTo('/segments', s('learnGoToSegments'))}
        />
      ),
    },
  ];

  // The section in the upper part of the window is the one being read
  useEffect(() => {
    if (typeof IntersectionObserver === 'undefined') return undefined;
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries.find((entry) => entry.isIntersecting);
        if (visible) setCurrent(visible.target.dataset.section);
      },
      { rootMargin: '-140px 0px -60% 0px' }
    );
    document
      .querySelectorAll('.learn__section')
      .forEach((section) => observer.observe(section));
    return () => observer.disconnect();
  }, []);

  const jump = (id) => {
    setCurrent(id);
    document
      .getElementById(`learn-${id}`)
      ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <Container>
      <div className="learn__intro">
        <h3 className="page-title">{s('learnMore')}</h3>
        <p>{s('learnMoreIntro')}</p>
      </div>

      <nav className="learn__nav" aria-label={s('learnMore')}>
        {sections.map((section) => (
          <button
            key={section.id}
            type="button"
            className={`learn__nav-item${
              current === section.id ? ' learn__nav-item--on' : ''
            }`}
            aria-current={current === section.id ? 'true' : undefined}
            onClick={() => jump(section.id)}
          >
            {section.label}
          </button>
        ))}
      </nav>

      {sections.map((section) => (
        <section
          key={section.id}
          id={`learn-${section.id}`}
          data-section={section.id}
          className="learn__section"
        >
          {section.content}
        </section>
      ))}
    </Container>
  );
};

export default LearnPage;
